import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { League } from '../types';

const AdminEpisodeManagement: React.FC = () => {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [deadlines, setDeadlines] = useState<{ [leagueId: string]: { [episode: string]: string } }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    const saved = localStorage.getItem('adminSelectedSeason');
    return saved ? parseInt(saved) : 49;
  });

  const numEpisodes = 14;

  const fetchLeagues = async (season: number) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`);
      if (!response.ok) throw new Error('Failed to fetch leagues');
      const data = await response.json();
      const filteredLeagues = (data.leagues || []).filter((l: any) => (l.season || 49) === season);
      setLeagues(filteredLeagues);
      
      const initialDeadlines: { [leagueId: string]: { [episode: string]: string } } = {};
      filteredLeagues.forEach((league: League) => {
        initialDeadlines[league.id] = league.episodeDeadlines || {};
      });
      setDeadlines(initialDeadlines);

    } catch (err: any) {
      setError(err.message || 'Could not load league data.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeagues(selectedSeason);

    const handleSeasonChange = () => {
      const newSeason = parseInt(localStorage.getItem('adminSelectedSeason') || '49');
      setSelectedSeason(newSeason);
      fetchLeagues(newSeason);
    };
    window.addEventListener('adminSeasonChanged', handleSeasonChange);
    return () => window.removeEventListener('adminSeasonChanged', handleSeasonChange);
  }, []);

  const handleDateChange = (leagueId: string, episode: number, date: string) => {
    setDeadlines(prev => ({
      ...prev,
      [leagueId]: {
        ...prev[leagueId],
        [episode]: date
      }
    }));
  };

  const saveDeadlines = async (leagueId: string) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/episode-deadlines`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deadlines: deadlines[leagueId] }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        try {
            const errData = JSON.parse(errorText);
            throw new Error(errData.message || 'Failed to save deadlines');
        } catch (e) {
            throw new Error(`Server returned a non-JSON error: ${errorText}`);
        }
      }
      setSuccess(`Deadlines for league ${leagueId} saved successfully!`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="mt-5 mb-5">
      <div className="mb-3">
        <Link to="/admin/dashboard" className="text-decoration-none">
          &larr; Back to Admin Home
        </Link>
      </div>
      <Card className="shadow-sm home-card">
        <Card.Header as="h4" className="card-title-custom text-center">Manage Medical Evacuation Swap Deadlines (Season {selectedSeason})</Card.Header>
        <Card.Body>
          <p className="text-center">Set the deadline (e.g., 7pm ET) for medical evacuation swaps. The deadline applies to the episode AFTER a player is evacuated.</p>
          {isLoading && <Spinner animation="border" />}
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          {leagues.map(league => (
            <Card key={league.id} className="mb-4">
              <Card.Header as="h6">{league.name}</Card.Header>
              <Card.Body>
                <Form>
                  {Array.from({ length: numEpisodes - 2 }, (_, i) => i + 2).map(episodeNum => (
                    <Form.Group as={Row} key={episodeNum} className="mb-3" controlId={`deadline-${league.id}-${episodeNum}`}>
                      <Form.Label column sm={5}>
                        Deadline for Swap after Episode {episodeNum}
                      </Form.Label>
                      <Col sm={7}>
                        <Form.Control
                          type="datetime-local"
                          value={deadlines[league.id]?.[episodeNum + 1] ? deadlines[league.id][episodeNum + 1].substring(0, 16) : ''}
                          onChange={e => handleDateChange(league.id, episodeNum + 1, e.target.value)}
                        />
                      </Col>
                    </Form.Group>
                  ))}
                  <div className="text-center">
                    <Button variant="primary" onClick={() => saveDeadlines(league.id)} disabled={isLoading}>
                      Save Deadlines for {league.name}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          ))}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AdminEpisodeManagement;